import { useSyncExternalStore } from 'react';
import { readStorage, writeStorage } from '@/lib/storage';

/**
 * The colour theme (D-343). The site opens LIGHT; the visitor can switch to dark with the button in the header and the
 * choice is remembered in `localStorage['sbg.theme']` (a row in the privacy notice). The theme lives on the root element
 * as `data-theme="dark"` (light is the absence of it, which is also what the pre-rendered HTML says), so the styles are
 * plain CSS custom properties (styles/tokens.css) and nothing re-renders to repaint. `public/theme-init.js` applies a
 * stored "dark" before the first paint, so a returning visitor never sees a flash of light.
 */

export type Theme = 'light' | 'dark';

export const THEME_KEY = 'sbg.theme';

/** `<meta name="theme-color">` per theme: the colour of the browser's own bar on phones. */
const BAR_COLOUR: Readonly<Record<Theme, string>> = { light: '#FFFFFF', dark: '#0B0B0C' };

const listeners = new Set<() => void>();

/** The theme the page shows now (light on the server and before anything is stored). */
export function getTheme(): Theme {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.dataset['theme'] === 'dark' ? 'dark' : 'light';
}

function paint(theme: Theme): void {
  const root = document.documentElement;
  if (theme === 'dark') root.dataset['theme'] = 'dark';
  else delete root.dataset['theme'];
  root.style.colorScheme = theme;
  root.style.backgroundColor = BAR_COLOUR[theme];
  document
    .querySelector<HTMLMetaElement>('meta[name="theme-color"]')
    ?.setAttribute('content', BAR_COLOUR[theme]);
}

/** Switch the theme, remember it, and tell the toggles. */
export function setTheme(theme: Theme): void {
  paint(theme);
  writeStorage(THEME_KEY, theme);
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // another tab changed it
  const onStorage = (event: StorageEvent): void => {
    if (event.key !== THEME_KEY) return;
    paint(event.newValue === 'dark' ? 'dark' : 'light');
    listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

/** The current theme for a component. The server and the first client render say "light" (what the HTML says). */
export function useTheme(): Theme {
  return useSyncExternalStore(subscribe, getTheme, () => 'light');
}

/** What is stored, for the rare caller that must know whether the visitor ever chose. */
export function storedTheme(): Theme | null {
  const value = readStorage(THEME_KEY);
  return value === 'dark' || value === 'light' ? value : null;
}
